import { useEffect, useState } from "react";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  InlineStack,
  Modal,
  Select,
  Text,
  TextField,
} from "@shopify/polaris";
import {
  comparisonRequest,
  type ComparisonAction,
} from "../../../../packages/shared/stock-comparison/client";
import type { ComparisonDetailData } from "../../../../packages/shared/stock-comparison/types";
import type { QueueAccount } from "../../../../packages/shared/shopify-queue/client";
const base = import.meta.env.VITE_API_URL || "https://api.whenlyapp.com";
const labels: Record<string, string> = {
  pending: "Waiting",
  matched: "Matched",
  different: "Different",
  needs_review: "Needs review",
};
const amount = (value: number | null) =>
  value === null ? "Not checked" : String(value);
export function ComparisonResults({
  account,
  id,
  refresh,
  locked,
  canPublish,
  onAction,
}: {
  account: QueueAccount;
  id: string;
  refresh: number;
  locked: boolean;
  canPublish: boolean;
  onAction: (action: ComparisonAction) => Promise<void>;
}) {
  const [data, setData] = useState<ComparisonDetailData | null>(null),
    [error, setError] = useState(""),
    [filter, setFilter] = useState("all"),
    [query, setQuery] = useState(""),
    [pages, setPages] = useState<number[]>([]),
    [tick, setTick] = useState(0),
    [selection, setSelection] = useState<number[]>([]),
    [action, setAction] = useState<"retry" | "cancel" | null>(null);
  const cursor = pages[pages.length - 1];
  useEffect(() => {
    const abort = new AbortController();
    setData(null);
    setError("");
    void comparisonRequest<ComparisonDetailData>(
      base,
      account,
      `/${encodeURIComponent(id)}?status=${filter}&q=${encodeURIComponent(query)}${cursor ? "&cursor=" + cursor : ""}`,
      { signal: abort.signal },
    )
      .then((result) => {
        if (!abort.signal.aborted) setData(result);
      })
      .catch((e) => {
        if (!abort.signal.aborted) setError(e.message);
      });
    return () => abort.abort();
  }, [account, id, refresh, filter, query, cursor, tick]);
  useEffect(() => {
    if (!data || !["pending", "running"].includes(data.run.state)) return;
    const timer = window.setInterval(() => setTick((v) => v + 1), 10000);
    return () => window.clearInterval(timer);
  }, [data?.run.state]);
  return (
    <Card>
      <BlockStack gap="300">
        <InlineStack gap="300">
          <Text as="h2" variant="headingMd">
            Comparison results
          </Text>
          <Button onClick={() => setTick((v) => v + 1)}>Refresh results</Button>
        </InlineStack>
        {error && <Banner tone="critical">{error}</Banner>}
        {!data && !error && <Text as="p">Loading comparison…</Text>}
        <Select
          label="Show comparison results"
          value={filter}
          options={[
            { label: "All results", value: "all" },
            ...Object.entries(labels).map(([value, label]) => ({
              value,
              label,
            })),
          ]}
          onChange={(value) => {
            setFilter(value);
            setPages([]);
          }}
        />
        <TextField
          label="Find a product by name, SKU or ID"
          autoComplete="off"
          maxLength={200}
          value={query}
          onChange={(value) => {
            setQuery(value);
            setPages([]);
          }}
        />
        {data && (
          <>
            <Text as="p">
              {data.run.domain} · {data.run.state} · Requested{" "}
              {data.run.created_at} UTC
            </Text>
            <Text as="p">
              {data.run.matched} matched · {data.run.different} different ·{" "}
              {data.run.needs_review} need review · {data.run.pending} not
              checked. {data.run.target_count} total product and location
              checks.
            </Text>
            <Text as="p">
              These are saved observations, not a live stock balance. Warehouse
              stock and Shopify may have changed since each check. A new
              comparison checks current values.
            </Text>
            {data.run.error && <Banner tone="warning">{data.run.error}</Banner>}
            <InlineStack gap="200">
              {data.run.state === "failed" && (
                <Button disabled={locked} onClick={() => setAction("retry")}>
                  Review retry
                </Button>
              )}
              {["pending", "running", "failed"].includes(data.run.state) && (
                <Button disabled={locked} onClick={() => setAction("cancel")}>
                  Review cancellation
                </Button>
              )}
            </InlineStack>
            <Text as="p">{data.total} checks match this filter.</Text>
            {canPublish && (
              <BlockStack gap="200">
                <Text as="p">
                  {selection.length} products selected. Only differences with
                  all product locations checked and no review issues can be
                  selected.
                </Text>
                {!!selection.length && (
                  <InlineStack gap="200">
                    <Button
                      url={
                        "/shopify-updates?connection_id=" + encodeURIComponent(data.run.connection_id) + "&product_ids=" + selection.join(",")
                      }
                    >
                      Review publishing selected products
                    </Button>
                    <Button onClick={() => setSelection([])}>
                      Clear selection
                    </Button>
                  </InlineStack>
                )}
                <Text as="p">
                  Publication uses fresh quantities for all mapped locations and
                  has a separate confirmation. This report stays as history.
                </Text>
              </BlockStack>
            )}
            {!data.rows.length && (
              <Text as="p">No results match this filter.</Text>
            )}
            {data.rows.map((row) => (
              <Card key={row.cursor}>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">
                    {row.sku || "#" + row.product_id} ·{" "}
                    {row.title || "Product no longer available"}
                  </Text>
                  <Text as="p">
                    {row.warehouse_names ||
                      "Warehouse mapping no longer available"}{" "}
                    · Shopify location {row.location_gid.split("/").pop()} ·{" "}
                    {labels[row.status] || row.status}
                  </Text>
                  <Text as="p">
                    Warehouse physical: {amount(row.physical)} · Reserved:{" "}
                    {amount(row.reserved)} · Reservations for imported Shopify
                    orders: {amount(row.shopify_reserved)}
                  </Text>
                  <Text as="p">
                    Free stock excluded from sale: {amount(row.unavailable??null)} ·
                    Expired free stock included in that total: {amount(row.expired_unreserved??null)}.
                    {row.company_day ? ` Expiry checked for ${row.company_day} (${row.time_zone}).` : ' No expiry check date was recorded.'}
                  </Text>
                  <Text as="p">
                    Shopify available: {amount(row.remote_available)} · Shopify
                    committed: {amount(row.remote_committed)} · Available to
                    publish: {amount(row.target_available)} · Difference:{" "}
                    {amount(row.difference)}
                  </Text>
                  <Text as="p">
                    {row.checked_at
                      ? "Checked " + row.checked_at
                      : "Not checked"}
                  </Text>
                  {row.error && <Banner tone="warning">{row.error}</Banner>}
                  {canPublish && !!row.publishable && (
                    <Checkbox
                      label={"Select product " + (row.sku || row.product_id)}
                      checked={selection.includes(row.product_id)}
                      disabled={
                        !selection.includes(row.product_id) &&
                        selection.length >= 500
                      }
                      onChange={(checked) =>
                        setSelection((ids) =>
                          checked
                            ? [...new Set([...ids, row.product_id])]
                            : ids.filter((value) => value !== row.product_id),
                        )
                      }
                    />
                  )}
                </BlockStack>
              </Card>
            ))}
            <InlineStack gap="200">
              {!!pages.length && (
                <Button onClick={() => setPages((p) => p.slice(0, -1))}>
                  Previous results
                </Button>
              )}
              {data.nextCursor && (
                <Button
                  onClick={() => setPages((p) => [...p, data.nextCursor!])}
                >
                  More results
                </Button>
              )}
            </InlineStack>
          </>
        )}
        <Modal
          title={
            action === "retry"
              ? "Retry stock comparison"
              : "Cancel stock comparison"
          }
          open={!!action}
          onClose={() => setAction(null)}
          primaryAction={{
            content:
              action === "retry"
                ? "Retry unfinished checks"
                : "Cancel comparison",
            disabled: locked || !data,
            onAction: () => {
              if (action && data) {
                const value = action;
                setAction(null);
                void onAction({
                  action: value,
                  run_id: data.run.id,
                  revision: data.run.revision,
                });
              }
            },
          }}
          secondaryActions={[
            { content: "Keep current state", onAction: () => setAction(null) },
          ]}
        >
          <Modal.Section>
            <Text as="p">
              {action === "retry"
                ? "Resume pending product and location checks using current stock. Already saved results keep their original check times."
                : "Stop remaining checks. Results already collected remain in the report."}{" "}
              Neither action changes stock in Whenly or Shopify.
            </Text>
          </Modal.Section>
        </Modal>
      </BlockStack>
    </Card>
  );
}
